<template>
    <div v-if="doctor !== null">
        <div class="doctorMenu">
            <doctor-info
                v-if="isDoctorInfo"
                :doctor="doctor"
                :user="user"
                :early-schedule="earlyScheduleComputed"
                with-doctor-profile-modal
                with-recently-consultation-date
                align-doctor-image="center">
            </doctor-info>

            <div
                v-for="menu in dMenus"
                :class="{ 'doctorMenuIsGrouped' : isGrouped }">
                <h1
                    v-if="menu.is_first_on_group === true"
                    class="doctorMenuIsGroupedTitle">
                    {{ menu.medical_course.name }}
                </h1>
                <div
                    class="doctorMenuRowWrapper d-block d-md-flex justify-content-between align-items-center"
                    @click="openModalReservationDoctorMenuDetail(menu)">
                    <div class="doctorMenuRowLeft">
                        <doctor-menu-base-info
                            :menu="menu"
                            :user="user"
                            :user-coupons="filteredUserCoupons"
                            :relative="false"
                            with-repeatable-date>
                        </doctor-menu-base-info>
                    </div>
                    <b-button class="doctorMenuButton">
                        日程を選ぶ
                    </b-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import constants from '../../constants'
    import YokumiruHelper from '../../YokumiruHelper'
    import { mapMutations } from 'vuex'

    export default {
        props: {
            doctorMenus: {
                type: Array,
                required: true,
            },
            isSearchCondition: {
                type: Boolean,
                default: false,
            },
            isDoctorInfo: {
                type: Boolean,
                default: false,
            },
            isGrouped: {
                type: Boolean,
                default: false,
            },
        },

        data: function () {
            return {
                helper: YokumiruHelper,
                constDoctorMenus: constants.doctor_menus,
            }
        },

        computed: {
            dMenus: function () {
                if (!this.isGrouped) {
                    return this.doctorMenus
                }
                let medicalCourseId = null
                return this.doctorMenus.map((menu) => {
                    if (menu.medical_course_id !== medicalCourseId) {
                        medicalCourseId = menu.medical_course_id
                        menu.is_first_on_group = true
                    }
                    return menu
                })
            },

            earlyScheduleComputed: function () {
                if (this.isSearchCondition) return null

                // 直接sortするとクーポン使用可能なメニューの優先表示ソートが崩れるためコピーする
                // @see https://github.com/sewii/Yokumiru/issues/977
                const doctorMenus = [...this.doctorMenus]

                // 相談時間が最も短いメニューから最短予約可能時間を判断する
                const menu = doctorMenus.sort((a, b) => {
                    if (a.consultation_minutes < b.consultation_minutes) return -1
                    if (a.consultation_minutes > b.consultation_minutes) return 1
                    return 0
                })
                return menu[0].early_schedule
            },

            doctor: function () {
                return this.doctorMenus[0].doctor
            },

            user: function () {
                return this.$store.state.reservation.user
            },

            /**
             * 所持クーポンのフィルタリング
             * （相談予約カテゴリのクーポンだけにする）
             *
             * @return {array}
             */
            filteredUserCoupons: function () {
                return this.helper.filteredUserCoupons(this.user)
            },
        },

        methods: {
            ...mapMutations({
                setMode: 'reservation/setMode',
                setMedicalCourse: 'reservation/setMedicalCourse',
                setDoctorMenu: 'reservation/setDoctorMenu',
                setAsRepeat: 'reservation/setAsRepeat',
            }),

            /**
             * メニュー詳細モーダル表示
             *
             * @param {object} doctorMenu
             */
            openModalReservationDoctorMenuDetail: function (doctorMenu) {
                this.pushAnalyticsDataLayerSelectDoctorMenu(doctorMenu)
                this.setMode('default')

                // 以前と異なるメニューが選択されたら問診内容をクリア
                const currentMenu = this.$store.state.reservation.doctorMenu
                if (!currentMenu || doctorMenu.id !== currentMenu.id) {
                    bus.$emit('clear-reservation-interview-questions')
                }

                const asRepeat = this.helper.isRepeatable(
                    doctorMenu,
                    this.$store.state.userConsultations.recentlyConsultations,
                    this.$store.state.reservation.user.timezone
                )
                this.setAsRepeat(asRepeat)
                this.setDoctorMenu(doctorMenu)

                // マッチング予約可能な相談科だが対象のメニューが無い場合、
                // MedicalCourse::filterForUser() で相談科一覧APIの返却値では can_matching フラグを折っているため、
                // 同じ相談科が既に vuex に含まれているなら can_matching を上書きしないようにスキップする
                const prevMedicalCourse = this.$store.state.reservation.medicalCourse
                if (!prevMedicalCourse || prevMedicalCourse.id !== doctorMenu.medical_course_id) {
                    this.setMedicalCourse(doctorMenu.medical_course)
                }

                bus.$emit('open-modal-reservation-doctor-menu-detail')
            },

            /**
             * GoogleAnalytics GA4 カスタムイベント発火
             *
             * @param {object} doctorMenu
             */
            pushAnalyticsDataLayerSelectDoctorMenu: function (doctorMenu) {
                if (this.$locale.type !== 'consul') return

                window.dataLayer = window.dataLayer || []
                window.dataLayer.push({
                    event: 'dl__selectDoctorMenu',
                    doctorMenuId: doctorMenu.id,
                    doctorMenuTitle: doctorMenu.title,
                })
            },
        },
    }
</script>

<style lang="scss" scoped>
.doctorMenuIsGrouped {
    &:nth-child(1) {
        .doctorMenuIsGroupedTitle {
            margin-top: 0;
        }
    }

    .doctorMenuIsGroupedTitle {
        margin-top: 35px;
        padding-bottom: 5px;
        color: #2A2E31;
        font-size: 20px;
        font-weight: bold;
        border-bottom: 1px solid #dee2e6;
    }
}

.doctorMenu {
    position: relative;
    display: block;
    vertical-align: top;
    width: 100%;
    margin: 15px auto;
    padding: 25px;
    background-color: #f9f9f9;
    border: 1px solid #dee2e6;
    border-radius: 6px;

    @media screen and (max-width: 767px) {
        padding: 15px;
    }

    .doctorMenuRowWrapper {
        position: relative;
        cursor: pointer;
        margin: 10px 0;
        padding: 15px 30px 10px 20px;
        background: #fff;
        border-radius: 8px;
        box-shadow: 3px 3px 0 #EBEBEB;
        transition: opacity 0.1s ease-in-out;

        @media screen and (max-width: 767px) {
            padding: 12px;
        }

        &:hover {
            opacity: 0.7;
        }

        .doctorMenuRowLeft {
            max-width: 75%;

            @media screen and (max-width: 767px) {
                max-width: 100%;
            }
        }
    }

    .doctorMenuButton {
        display: block;
        width: 100px;
        height: 38px;
        line-height: 38px;
        padding: 0;
        color: #fff;
        font-weight: bold;
        text-align: center;
        background-color: #CF798A;
        border: none;
        border-radius: 8px;
        cursor: pointer;

        @media screen and (max-width: 767px) {
            margin: 15px auto 0 auto;
        }
    }
}
</style>
